import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Button, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { getStyles } from './styles';

import PermissionService from '../../services/PermissionService';
import { AccessLevel, OrgPermissionDTO, PermissionCode, PermissionDTO, UserRole } from '@beable/roster-backend-client-v1';
import BaseSheet from '../Common/Layout/BaseSheet';
import ProvisionTree from '../Provision/components/ProvisionTree';
import isEqual from 'lodash/isEqual';
import difference from 'lodash/difference';
import { Alert } from '@mui/material';
import BaseDialog from '../Common/BaseDialog';

interface PermissionSheetProps {
    siteId: number | undefined;
    permissionCode: PermissionCode | undefined;
    role: UserRole | undefined;
}

export default function PermissionsSheet({ siteId, permissionCode, role }: PermissionSheetProps) {
    const [schools, setSchools] = useState<OrgPermissionDTO[]>([]);
    const [preSelectedNodeValues, setPreSelectedNodeValues] = useState<string[]>([]);
    const [schoolsWithPermission, setSchoolsWithPermission] = useState<string[]>([]);
    const [isError, setIsError] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const theme = useTheme();
    const styles = getStyles(theme);

    useEffect(() => {
        if (!siteId || !permissionCode || !role) {
            return;
        }

        const container = document.getElementById('customer-permissions-container');
        if (container) container.scrollTo(0, 0);

        const fetchPermissionForSchools = async () => {
            const data = await PermissionService.getPermissionForSiteOrgs(siteId, permissionCode, role);

            if (data) {
                setSchools(data);
            }
        };

        fetchPermissionForSchools();
    }, [siteId, permissionCode, role]);

    useEffect(() => {
        setPreSelectedNodeValues(schools.filter((school) => school.permissionEnabled).map((s) => s.id));
    }, [schools])

    const nodes = useMemo(
        () => schools.map((s) => ({ value: s.id, label: s.name })),
        [schools]
    );

    const isPermissionChanged: boolean = useMemo(
        () => !isEqual(preSelectedNodeValues.sort(), schoolsWithPermission.sort()),
        [preSelectedNodeValues, schoolsWithPermission]
    );

    const handleSave = async () => {
        setIsSaving(true);
        setIsError(false);

        if (!siteId || !permissionCode || !role) {
            return;
        }

        const permissions = getPermissionsToSet(schools, preSelectedNodeValues, schoolsWithPermission);
        const response = await PermissionService.setPermissionForSiteOrgs(siteId, permissionCode, role, permissions);
        setPreSelectedNodeValues(schoolsWithPermission);

        if (response.isErr()) {
            setIsError(true);
            return;
        } else {
            setIsSuccess(true);
        }
         
        setIsSaving(false);
    }

    const getPermissionsToSet = (
        schools: OrgPermissionDTO[],
        initialSelected: string[],
        newSelected: string[]
    ): PermissionDTO[] => {
        if (!siteId) {
            return [];
        }

        // site level enabled:
        if (initialSelected.length === 0 && newSelected.length === schools.length) {
            return [{
                accessLevel: AccessLevel.Site,
                identifier: siteId.toString(),
                enabled: true
            }];
        }

        // site level disabled:
        if (initialSelected.length === schools.length && newSelected.length === 0) {
            return [{
                accessLevel: AccessLevel.Site,
                identifier: siteId.toString(),
                enabled: false
            }];
        }

        // orgs disabled:
        const schoolsToDisable = difference(initialSelected, newSelected).map(schoolId => ({
            accessLevel: AccessLevel.Org,
            identifier: schoolId,
            enabled: false
        }));
        const schoolsToEnable = difference(newSelected, initialSelected).map(schoolId => ({
            accessLevel: AccessLevel.Org,
            identifier: schoolId,
            enabled: true
        }));

        return [...schoolsToDisable, ...schoolsToEnable];
    }

    const isSaveDisabled = (): boolean => {
        return !schools.length || isSaving || !isPermissionChanged;
    }

    const clearAlertState = (): void => {
        setIsError(false);
        setIsSuccess(false);
    }

    const getDialogText = (): string => {
        if (isError) {
            return "Error saving permissions. Please Try Again!";
        }

        if (isSuccess) {
            return "Permissions saved successfully!"
        }

        return "";
    }

    return (
        <BaseSheet id='custsomer-permissions-container'>
            <Typography variant='h6' sx={styles.header}>
                Customer Permissions
            </Typography>
            <ProvisionTree
                nodes={nodes}
                preSelectedNodeValues={preSelectedNodeValues}
                onChange={(newSelectedNodes) => {
                    if (isEqual(newSelectedNodes.sort(), schoolsWithPermission.sort())) {
                      return;
                    }
                    setSchoolsWithPermission(newSelectedNodes);
                }}        
                rosterWarning={''}
                isLoading={false}
                filterGrades={undefined}
                hideExpandAll
            />
            <Grid container sx={{
                justifyContent: 'space-between'
            }}>
                <Button
                    variant='contained'
                    color='primary'
                    aria-label='save'
                    title='Save'
                    sx={styles.button}
                    onClick={handleSave}
                    disabled={isSaveDisabled()}
                >
                    Save Permission
                </Button>
            </Grid>
            <BaseDialog
              open={isError || isSuccess}
              showCloseIcon={false}
              showCloseButton={true}
              onClose={clearAlertState}
            >
                <Alert severity={isError ? "error" : "success"} sx={styles.alert}>
                    {getDialogText()}
                </Alert>
            </BaseDialog>
        </BaseSheet>
    );
}

